// localStorage helpers for TV app state

export const getScreenToken = (): string | null =>
  localStorage.getItem('screen_token')

export const setScreenToken = (token: string) =>
  localStorage.setItem('screen_token', token)

export const clearScreenToken = () =>
  localStorage.removeItem('screen_token')

export const getRoomId = (): string | null =>
  localStorage.getItem('room_id')

export const setRoomId = (roomId: string) =>
  localStorage.setItem('room_id', roomId)

export const getTenantInfo = (): { name: string; primaryColor: string } | null => {
  const raw = localStorage.getItem('tenant_info')
  return raw ? JSON.parse(raw) : null
}

export const setTenantInfo = (info: { name: string; primaryColor: string }) =>
  localStorage.setItem('tenant_info', JSON.stringify(info))

// ─── Screen / Room identity ───────────────────────────────────────────────────
// The paired screen has a human-readable name ("Main Studio", "Ring 2") + its
// room's name. We surface these in the TV chrome so coaches / athletes /
// admins can tell at a glance which physical display they're looking at —
// especially useful when several TVs show similar content side-by-side.

export interface ScreenInfo {
  screenName: string
  roomName: string | null
}

export const getScreenInfo = (): ScreenInfo | null => {
  const raw = localStorage.getItem('screen_info')
  return raw ? JSON.parse(raw) : null
}

export const setScreenInfo = (info: ScreenInfo) =>
  localStorage.setItem('screen_info', JSON.stringify(info))

export const clearScreenInfo = () => localStorage.removeItem('screen_info')

export const getLastSessionState = (): string | null =>
  localStorage.getItem('last_session_state')

export const setLastSessionState = (state: unknown) => {
  try { localStorage.setItem('last_session_state', JSON.stringify(state)) } catch { /* quota or serialize error — ignore */ }
}

export const clearLastSessionState = () =>
  localStorage.removeItem('last_session_state')

// ─── Last ended session — used to restore split screen after refresh ──────────
//
// When a session ends while the TV is showing the split screen (ENDED + upcoming
// PRESTART), a browser refresh loses the ended session from memory.  We persist
// it here so fetchActiveAndSnapshot() can reconstruct the split screen even if
// the Redis key has already been cleared.

const LAST_ENDED_SESSION_KEY = 'gb_last_ended_session'

export interface LastEndedSessionRecord {
  sessionJson: string     // JSON of the LiveSession object
  leaderboardJson: string | null  // JSON of the final LeaderboardState (or null)
  endedAt: number         // Date.now() when the session ended
  roomId: string
}

export const setLastEndedSession = (record: LastEndedSessionRecord): void => {
  try { localStorage.setItem(LAST_ENDED_SESSION_KEY, JSON.stringify(record)) } catch { /* ignore */ }
}

export const getLastEndedSession = (): LastEndedSessionRecord | null => {
  try {
    const raw = localStorage.getItem(LAST_ENDED_SESSION_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

export const clearLastEndedSession = (): void => {
  try { localStorage.removeItem(LAST_ENDED_SESSION_KEY) } catch { /* ignore */ }
}

/**
 * The server no longer accepts this TV's token: an admin issued a new
 * pairing code (that is how a lost or moved TV is revoked) or the token
 * expired. Forget the pairing and start over at the pairing screen —
 * otherwise the TV sat on "Reconnecting…" forever.
 */
export const forgetPairing = () => {
  try {
    for (const key of ['screen_token', 'room_id', 'screen_info', 'tenant_info', 'last_session_state']) {
      localStorage.removeItem(key)
    }
  } catch { /* storage unavailable */ }
  window.location.reload()
}
